import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import axiosInstance from "../api";
import { buildLoginUrl, REDIRECT_URI, PKCE_STATE_KEY, PKCE_VERIFIER_KEY, PKCE_PROVIDER_KEY } from "../actions/constants";
import { useUserStore } from "./useUserStore";

// Estado inicial del store
const initialState = {
  oauth: {
    state: null,
    codeVerifier: null,
    provider: null,
    callbackParams: null,
    stateValid: null,
  },
  session: {
    user: null,
    authenticationToken: null,
  },
};

// Canal para sincronizar logout entre pestañas
const AUTH_BROADCAST_CHANNEL = "auth";
const bc = typeof window !== "undefined" && "BroadcastChannel" in window ? new BroadcastChannel(AUTH_BROADCAST_CHANNEL) : null;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      // Inicio de sesión OAuth
      startLogin: async (provider = "google") => {
        const url = await buildLoginUrl(provider);

        const storedState = sessionStorage.getItem(PKCE_STATE_KEY);
        const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

        set({
          oauth: {
            ...get().oauth,
            state: storedState || null,
            codeVerifier: codeVerifier || null,
            provider,
          },
        });

        window.location.assign(url);
      },

      captureOAuthCallback: ({ code, state, provider, redirect_uri, raw_params, code_verifier, state_valid }) => {
        set({
          oauth: {
            ...get().oauth,
            provider,
            callbackParams: {
              code,
              state,
              provider,
              redirect_uri,
              raw_params,
              code_verifier,
            },
            stateValid: !!state_valid,
          },
        });
      },

      // Intercambia el code por la sesión
      finishLogin: async () => {
        const { oauth } = get();
        const { callbackParams, stateValid } = oauth || {};
        if (!callbackParams?.code || !stateValid) {
          throw new Error("Callback OAuth inválido.");
        }

        const providerFromState = oauth?.provider || callbackParams?.provider || "google";

        const payload = {
          code: callbackParams.code,
          redirect_uri: REDIRECT_URI,
          type_of_account: providerFromState,
          code_verifier: callbackParams.code_verifier || sessionStorage.getItem(PKCE_VERIFIER_KEY) || null,
        };

        const { data } = await axiosInstance.post("/sessions", payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 15000,
        });

        const { user, authenticationToken } = data || {};

        set({
          session: { user, authenticationToken },
          oauth: { ...initialState.oauth },
        });

        try {
          sessionStorage.removeItem(PKCE_STATE_KEY);
          sessionStorage.removeItem(PKCE_VERIFIER_KEY);
          sessionStorage.removeItem(PKCE_PROVIDER_KEY);
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("Error al limpiar PKCE en sessionStorage:", e);
          }
        }

        return { user, authenticationToken };
      },

      logout: async () => {
        const prevToken = get()?.session?.authenticationToken;
        set({ ...initialState });
        try {
          sessionStorage.removeItem(PKCE_STATE_KEY);
          sessionStorage.removeItem(PKCE_VERIFIER_KEY);
          sessionStorage.removeItem(PKCE_PROVIDER_KEY);
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("Error al limpiar PKCE en logout:", e);
          }
        }
        try {
          useUserStore.getState().reset?.();
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("Error al resetear useUserStore:", e);
          }
        }
        try {
          bc?.postMessage({ type: "logout" });
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("Error al enviar broadcast de logout:", e);
          }
        }
        (async () => {
          try {
            if (prevToken) {
              await axiosInstance.delete("/sessions", {
                headers: { Authorization: `Bearer ${prevToken}` },
                timeout: 10000,
              });
            } else {
              await axiosInstance.delete("/sessions", { timeout: 10000 });
            }
          } catch (e) {
            if (process.env.NODE_ENV !== "production") {
              console.debug("Error al revocar sesión en backend:", e);
            }
          }
        })();
      },
    }),
    {
      name: "cuestionarios-auth",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ session: state.session }),
      onRehydrateStorage: () => () => {},
    },
  ),
);

// Listener de broadcast para cerrar sesión si otra pestaña lo hace
if (bc) {
  try {
    bc.onmessage = (event) => {
      if (event?.data?.type === "logout") {
        useAuthStore.setState({ ...initialState }, true);
        try {
          sessionStorage.removeItem(PKCE_STATE_KEY);
          sessionStorage.removeItem(PKCE_VERIFIER_KEY);
          sessionStorage.removeItem(PKCE_PROVIDER_KEY);
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("Error al limpiar PKCE por broadcast:", e);
          }
        }
        try {
          useUserStore.getState().reset?.();
        } catch (e) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("Error al resetear useUserStore por broadcast:", e);
          }
        }
      }
    };
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("Error al configurar BroadcastChannel:", e);
    }
  }
}
